import {Injectable} from '@angular/core';
import {BehaviorSubject} from "rxjs";

export interface CartItem {
  inventoryItemId: string;
  itemName: string;
  metalDesignName: string;
  metalType: string;
  metalPurity: string;
  format: string;
  denomination: number;
  quantity: number;
  makingChargePct: number;
  hasImage?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {

  private static readonly STORAGE_KEY = 'directSaleCart';
  private static readonly TARGET_USER_KEY = 'directSaleTargetUserId';

  private cartCountSubject = new BehaviorSubject<number>(this.calculateItemCount());

  cartCount$ = this.cartCountSubject.asObservable();

  setTargetUserId(userId: string | null): void {
    if (userId) {
      localStorage.setItem(CartService.TARGET_USER_KEY, userId);
    } else {
      localStorage.removeItem(CartService.TARGET_USER_KEY);
    }
  }

  getTargetUserId(): string | null {
    return localStorage.getItem(CartService.TARGET_USER_KEY);
  }

  addItem(item: CartItem): void {
    const items = this.getItems();
    const existing = items.find(i => i.inventoryItemId === item.inventoryItemId);
    if (existing) {
      existing.quantity += item.quantity;
      existing.hasImage = item.hasImage;
    } else {
      items.push({...item});
    }
    this.saveItems(items);
  }

  removeItem(inventoryItemId: string): void {
    let items = this.getItems();
    items = items.filter(i => i.inventoryItemId !== inventoryItemId);
    this.saveItems(items);
  }

  updateQuantity(inventoryItemId: string, qty: number): void {
    const items = this.getItems();
    const existing = items.find(i => i.inventoryItemId === inventoryItemId);
    if (existing) {
      if (qty <= 0) {
        this.removeItem(inventoryItemId);
        return;
      }
      existing.quantity = qty;
      this.saveItems(items);
    }
  }

  getItems(): CartItem[] {
    const raw = localStorage.getItem(CartService.STORAGE_KEY);
    if (!raw) {
      return [];
    }
    try {
      return JSON.parse(raw) as CartItem[];
    } catch {
      return [];
    }
  }

  clearCart(): void {
    localStorage.removeItem(CartService.STORAGE_KEY);
    localStorage.removeItem(CartService.TARGET_USER_KEY);
    this.cartCountSubject.next(0);
  }


  private saveItems(items: CartItem[]): void {
    localStorage.setItem(CartService.STORAGE_KEY, JSON.stringify(items));
    this.cartCountSubject.next(this.calculateItemCount(items));
  }

  private calculateItemCount(items?: CartItem[]): number {
    const cartItems = items || this.getItems();
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }

}
